// Your history as Top picks reads it (Oct 2026): the tunes you opened, saved
// and filed, matched to the library's tunes by the same rules the library
// merges versions by (`libraryTuneMatcher`), each weighed by how strongly it
// says you play it. Nothing here is new data: recents, saved tunes and
// playlists are already in the store.
//
// The weights, from the discovery plan (§4.2): the newest recent counts 1,
// each older one 0.85 of the one before; a saved copy 0.8 (you kept it); a
// playlist row 0.5, up to 1.5 for a tune filed in several; a tune the band has
// played two minutes or more 1, up to 1.5 at twenty minutes, fading with a
// 30-day half-life since it last played; a loved tune 1.5, as much as the
// strongest of those. A tune counts once, its weights summed. A Suggest less
// mark (retired Oct 2026, user: "I don't even like the suggest less option")
// counts nothing: marks made before stay in storage and are ignored.

import type { HistoryEntry } from '@/music/recommend';
import { PLAYED_SECONDS, type TuneHistory } from '@/state/tuneHistory';
import type { TuneTaste } from '@/state/tuneTaste';
import type { PlaylistTuneRef } from '@/storage/playlists';

export interface TasteInputs {
  /** Newest first, as the store keeps them. */
  recentTunes: readonly PlaylistTuneRef[];
  savedCharts: readonly { title: string; composer?: string }[];
  playlists: readonly { entries: readonly { ref: PlaylistTuneRef }[] }[];
  /** How long you played each tune (state/tuneHistory.ts). */
  tuneHistory?: TuneHistory;
  /** The tunes you loved (state/tuneTaste.ts). */
  tuneTaste?: TuneTaste;
  /** Now, in epoch milliseconds: what play time fades from. */
  now?: number;
}

const RECENT_DECAY = 0.85;
const SAVED = 0.8;
const FILED = 0.5;
const FILED_MOST = 1.5;
const PLAYED = 1;
const PLAYED_MORE = 0.5;
const PLAYED_LONG_SECONDS = 1200;
const HALF_LIFE_MS = 30 * 86_400_000;
const LOVED = 1.5;

type Find = (title: string, composer?: string) => number | null;

export function tasteHistory(inputs: TasteInputs, find: Find): HistoryEntry[] {
  const weights = new Map<number, number>();
  const add = (tune: number | null, weight: number) => {
    if (tune !== null) weights.set(tune, (weights.get(tune) ?? 0) + weight);
  };
  inputs.recentTunes.forEach((ref, i) => add(find(ref.title, ref.composer), RECENT_DECAY ** i));
  for (const saved of inputs.savedCharts) add(find(saved.title, saved.composer), SAVED);
  const filed = new Map<number, number>();
  for (const list of inputs.playlists) {
    for (const { ref } of list.entries) {
      const tune = find(ref.title, ref.composer);
      if (tune !== null) filed.set(tune, Math.min(FILED_MOST, (filed.get(tune) ?? 0) + FILED));
    }
  }
  for (const [tune, weight] of filed) add(tune, weight);
  for (const entry of Object.values(inputs.tuneHistory ?? {})) {
    if (entry.playSeconds < PLAYED_SECONDS) continue;
    const age = inputs.now !== undefined && entry.lastPlayed ? inputs.now - entry.lastPlayed : 0;
    const fade = 0.5 ** (Math.max(0, age) / HALF_LIFE_MS);
    const length = Math.min(1, entry.playSeconds / PLAYED_LONG_SECONDS);
    add(find(entry.title, entry.composer), (PLAYED + PLAYED_MORE * length) * fade);
  }
  for (const mark of inputs.tuneTaste ?? [])
    if (mark.verdict === 'love') add(find(mark.title, mark.composer), LOVED);
  return [...weights].map(([tune, weight]) => ({ tune, weight }));
}
